import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { ContentSchemaRender } from "~/components/content/content-schema-render";
import { SupportedContentNode } from "~/components/content/content-schema-render/enums";
import { checkSchemaNodeHasScmpYoutubeVideo } from "~/components/content/content-schema-render/iframe/helpers";
import { useContentSchemaRenderIframe } from "~/components/content/content-schema-render/iframe/hooks";
import type { articleLeadingArticle$key } from "~/queries/__generated__/articleLeadingArticle.graphql";

import type { Props as ArticleLeadingImagesProps } from "./article-leading-images";
import { ArticleLeadingImages } from "./article-leading-images";
import { ArticleLeadingVideoContainer } from "./styles";

export type Props = {
  reference: articleLeadingArticle$key;
} & Omit<ArticleLeadingImagesProps, "reference" | "imageProps">;

export const ArticleLeading: FunctionComponent<Props> = ({
  reference: reference_,
  ...articleLeadingProps
}) => {
  const article = useFragment(
    graphql`
      fragment articleLeadingArticle on Article {
        multimediaEmbed {
          json
        }
        ...articleLeadingImagesContent
        ...contentSchemaRenderContent
      }
    `,
    reference_,
  );

  const { multimediaEmbed } = article;

  // Check multimediaEmbed has youtube video in the node
  const isYoutubeVideo = useMemo(() => {
    if (!multimediaEmbed?.json) return false;
    return multimediaEmbed?.json?.some(node => checkSchemaNodeHasScmpYoutubeVideo(node)) ?? false;
  }, [multimediaEmbed]);

  const { withExtraProps: iframeWithExtraProps } = useContentSchemaRenderIframe();
  if (isYoutubeVideo)
    return (
      <ArticleLeadingVideoContainer>
        <ContentSchemaRender
          extraRenderMap={{
            [SupportedContentNode.Iframe]: iframeWithExtraProps({
              gaActionPrefix: "Youtube article header",
            }),
          }}
          reference={article}
          schema={multimediaEmbed?.json}
        />
      </ArticleLeadingVideoContainer>
    );

  return (
    <ArticleLeadingImages
      {...articleLeadingProps}
      imageProps={{ lazyload: false }}
      reference={article}
    />
  );
};

ArticleLeading.displayName = "ArticleLeading";
